import React, { useEffect, useState } from "react";
import qs from "qs";
import swal from "sweetalert2";
import { useNavigate, Link } from "react-router-dom";
import { URL } from "../../../constants/URL";
import CS from "../../../services/commonService";
import BreadCrumbs from "../../common/breadcrumbs";
import SearchBox from "../../common/searchBox";
import Table from "../../common/table";
import {
	useGetAllRestaurants,
	useUpdateRestaurant,
	useDeleteRestaurant,
	useDownloadRestaurants,
	useDeactivateRestaurant,
	useReactivateRestaurant,
} from "../../../queryHooks";
import Icon from "../../icons/index";
import ReactTooltip from "react-tooltip";
import { getCurrentTimezoneDate } from "../../../util/columnsOrdering";
import SingleStatusToggle from "../../common/singleStatusToggle";
import { useStore } from "../../../zustand/store";
import { saveAs } from "file-saver";
import { modules } from "../../../constants/Modules";
//	Pre-defined values
const columnKey = "restaurants";
const options = [
	{ value: "PENDING", label: "Pending" },
	{ value: "APPROVED", label: "Approved" },
	{ value: "REJECTED", label: "Rejected" },
];
const className = "drop-up";
//	Component starts
const Restaurants = () => {
	//	Redux-store
	const setLoading = useStore((state) => state.setLoading);
	const lastPageData = useStore((state) => state.lastPageData);
	const setLastPageData = useStore((state) => state.setLastPageData);
	const navigate = useNavigate();
	//	State values
	const [tableData, setTableData] = useState();
	const [pageNow, setPageNow] = useState(
		lastPageData?.[modules.RESTAURANTS]?.page || 1
	);
	const [itemsPerPage, setItemsPerPage] = useState(
		lastPageData?.[modules.RESTAURANTS]?.items_per_page || 10
	);
	const [restaurantPayload, setRestaurantPayload] = useState(
		lastPageData?.[modules.RESTAURANTS] || {
			pagination: true,
			items_per_page: itemsPerPage,
			page: pageNow,
			sort: { "data_restaurants.created_at": "DESC" },
		}
	);

	/******************************************************************************************
	@Purpose    :   To get the restaurants list
	*******************************************************************************************/

	const {
		data: dataGetAllRestaurants,
		isLoading,
		refetch,
	} = useGetAllRestaurants(qs.stringify(restaurantPayload));

	useEffect(() => {
		if (dataGetAllRestaurants && dataGetAllRestaurants.status === "success") {
			let respData = dataGetAllRestaurants.data;
			respData.page = pageNow;
			respData.items_per_page = itemsPerPage;
			setTableData(respData);
		}
	}, [dataGetAllRestaurants]);

	const {
		mutateAsync: updateRestaurant,
		error: errorUpdateRestaurant,
		data: dataUpdateRestaurant,
	} = useUpdateRestaurant();

	useEffect(() => {
		if (dataUpdateRestaurant) {
			setLoading(false);
			if (dataUpdateRestaurant.status === "success") {
				CS.showSuccessMessage(dataUpdateRestaurant.message);
				refetch();
			} else if (dataUpdateRestaurant.status === "fail") {
				CS.showErrorMessage(dataUpdateRestaurant.message);
			}
		}
	}, [dataUpdateRestaurant]);

	useEffect(() => {
		if (errorUpdateRestaurant) {
			setLoading(false);
			CS.showErrorMessage(errorUpdateRestaurant.response?.data?.message);
		}
	}, [errorUpdateRestaurant]);

	const {
		mutateAsync: deleteRestaurant,
		error: errorDeleteRestaurant,
		data: dataDeleteRestaurant,
	} = useDeleteRestaurant();

	useEffect(() => {
		if (dataDeleteRestaurant) {
			setLoading(false);
			if (dataDeleteRestaurant.status === "success") {
				CS.showSuccessMessage(dataDeleteRestaurant.message);
				refetch();
			} else if (dataDeleteRestaurant.status === "fail") {
				CS.showErrorMessage(dataDeleteRestaurant.message);
			}
		}
	}, [dataDeleteRestaurant]);

	useEffect(() => {
		if (errorDeleteRestaurant) {
			setLoading(false);
			CS.showErrorMessage(errorDeleteRestaurant.response?.data?.message);
		}
	}, [errorDeleteRestaurant]);

	const {
		mutateAsync: downloadRestaurants,
		data: dataDownloadRestaurants,
		isLoading: isLoadingDownload,
	} = useDownloadRestaurants();

	useEffect(() => {
		if (dataDownloadRestaurants) {
			saveAs(dataDownloadRestaurants.data?.filePath, "restaurants.xlsx");
		}
	}, [dataDownloadRestaurants]);

	const {
		mutateAsync: deactivateRestaurantAsync,
		error: errorDeactivateRestaurant,
		data: dataDeactivateRestaurant,
	} = useDeactivateRestaurant();

	useEffect(() => {
		if (dataDeactivateRestaurant) {
			setLoading(false);
			if (dataDeactivateRestaurant.status === "success") {
				CS.showSuccessMessage(dataDeactivateRestaurant.message);
				refetch();
			} else if (dataDeactivateRestaurant.status === "fail") {
				CS.showErrorMessage(dataDeactivateRestaurant.message);
			}
		}
	}, [dataDeactivateRestaurant]);

	useEffect(() => {
		if (errorDeactivateRestaurant) {
			setLoading(false);
			CS.showErrorMessage(errorDeactivateRestaurant.response?.data?.message);
		}
	}, [errorDeactivateRestaurant]);

	const {
		mutateAsync: reactivateRestaurantAsync,
		error: errorReactivateRestaurant,
		data: dataReactivateRestaurant,
	} = useReactivateRestaurant();

	useEffect(() => {
		if (dataReactivateRestaurant) {
			setLoading(false);
			if (dataReactivateRestaurant.status === "success") {
				CS.showSuccessMessage(dataReactivateRestaurant.message);
				refetch();
			} else if (dataReactivateRestaurant.status === "fail") {
				CS.showErrorMessage(dataReactivateRestaurant.message);
			}
		}
	}, [dataReactivateRestaurant]);

	useEffect(() => {
		if (errorReactivateRestaurant) {
			setLoading(false);
			CS.showErrorMessage(errorReactivateRestaurant.response?.data?.message);
		}
	}, [errorReactivateRestaurant]);

	const columns = [
		{
			label: "Joined Date",
			type: "date",
			sort: true,
			path: "created_at",
			alias: "data_restaurants.created_at",
			filterDate: true,
			content: (item) => getCurrentTimezoneDate(item.created_at),
		},
		{
			label: "Restaurant Name",
			type: "string",
			path: "name",
			alias: "data_restaurants.name",
			sort: true,
			content: (item) => (
				<Link
					to={`${URL.restaurants}/${item.id}`}
					onClick={() => {
						setLastPageData({ [modules.RESTAURANTS]: restaurantPayload });
					}}
				>
					<div className="d-flex align-items-center">
						<div className="flex-auto">
							{CS.renderImageUrl(item.profile_image_url)}
						</div>
						<div className="d-flex flex-column flex-wrap">
							<span className="pl-2">{item.name}</span>
						</div>
					</div>
				</Link>
			),
		},
		{
			label: "Email",
			type: "string",
			// sort: true,
			path: "email",
			// alias: "email",
			content: (item) => (
				<div className="email-col d-flex align-items-center">
					{/* <div className="badge-outer circle round d-flex align-items-center mr-2">
						<span
							className={
								item?.is_email_verified === 1
									? "badge badge-success circle"
									: "badge badge-danger circle"
							}
						></span>
					</div> */}
					<span>{item.email}</span>
				</div>
			),
		},
		{
			label: "Phone No",
			type: "string",
			// sort: true,
			path: "mobile",
			// alias: "mobile",
		},
		{
			label: "Business Type",
			type: "string",
			// sort: true,
			path: "business_type",
			aliasFilter: "business_type",
			filterOptions: [
				{ value: "INDIVIDUAL", label: "Individual" },
				{ value: "FRANCHISE", label: "Franchise" },
			],
		},
		{
			label: "Featured",
			type: "boolean",
			aliasFilter: "data_restaurants.is_featured",
			filterOptions: [
				{ value: 1, label: "Featured" },
				{ value: 0, label: "Unfeatured" },
			],
			content: (item) => (
				<SingleStatusToggle
					_id={`${item.id}featured`}
					status={item.is_featured == 1 ? true : false}
					onToggleChange={() => {
						const fileData = new FormData();
						fileData.append(
							"is_featured",
							item.is_featured === 0 ? true : false
						);
						updateRestaurant({
							id: item.id,
							fileData,
						});
						setLoading(true);
					}}
					htmlFor={`${item.id}featured`}
				/>
			),
		},
		{
			label: "Status",
			type: "boolean",
			aliasFilter: "data_restaurants.is_active",
			filterOptions: [
				{ value: 1, label: "Unblocked" },
				{ value: 0, label: "Blocked" },
			],
			content: (item) => (
				<SingleStatusToggle
					_id={item.id}
					status={item.is_active == 1 ? true : false}
					onToggleChange={() => {
						if (item.is_active == 1) {
							deactivateRestaurantAsync(item.id);
							setLoading(true);
						} else {
							reactivateRestaurantAsync(item.id);
							setLoading(true);
						}
					}}
					htmlFor={item.id}
				/>
			),
		},
		{
			label: "Verification Status",
			type: "boolean",
			aliasFilter: "admin_approval_status",
			filterOptions: options,
			content: (item) =>
				item.admin_approval_status === "PENDING" ? (
					<p className="badge badge-light-warning mb-0">Pending</p>
				) : // <Select
				// 	className={"badge badge-pending " + className}
				// 	value={options.find(
				// 		(option) => option.value === item.admin_approval_status
				// 	)}
				// 	onChange={(option) => {
				// 		const fileData = new FormData();
				// 		fileData.append("admin_approval_status", option.value);
				// 		updateRestaurant({
				// 			id: item.id,
				// 			fileData,
				// 		});
				// 		setLoading(true);
				// 	}}
				// 	options={options}
				// />
				item.admin_approval_status === "APPROVED" ? (
					<p className="badge badge-light-success mb-0">Approved</p>
				) : (
					<p className="badge badge-light-danger mb-0">Rejected</p>
				),
		},
		{
			label: "Action",
			type: "boolean",
			content: (item) => (
				<>
					<Icon
						type="show"
						data-tip
						data-for="show"
						onClick={() => {
							setLastPageData({ [modules.RESTAURANTS]: restaurantPayload });
							navigate(`${URL.restaurants}/${item.id}`);
						}}
					/>
					<ReactTooltip id="show" place="top" effect="solid">
						Show
					</ReactTooltip>
					<Icon
						type="delete"
						data-tip
						data-for="delete"
						// onClick={() =>
						// 	swal
						// 		.fire({
						// 			title: "Are you sure, you want to delete this restaurant?",
						// 			text: `You won't be able to revert this!`,
						// 			icon: "warning",
						// 			showCancelButton: true,
						// 			confirmButtonColor: "#3085d6",
						// 			cancelButtonColor: "#d33",
						// 			confirmButtonText: "Yes, delete it!",
						// 		})
						// 		.then((result) => {
						// 			if (result.isConfirmed) {
						// 				deleteRestaurant(item.id);
						// 				setLoading(true);
						// 			}
						// 		})
						// }
					/>
					<ReactTooltip id="delete" place="top" effect="solid">
						Delete
					</ReactTooltip>
				</>
			),
		},
	];

	return (
		<>
			<BreadCrumbs
				title="Restaurants Management"
				data={[{ label: "Restaurants" }]}
			/>
			<div className="dataTables_wrapper">
				<div className="content-area position-relative">
					<div className="grid">
						<div className="grid-content">
							<div className="user-listing-filterOptions mb-2 mt-3 d-block">
								<div className="row mb-2">
									<div className="col-sm-8 mb-2 position-static">
										<div className="left-buttons d-flex">
											<SearchBox
												columnKey={columnKey}
												searchValue={
													lastPageData?.[modules.RESTAURANTS]?.search?.[
														"data_restaurants.name"
													]
												}
												search={(value) => {
													setPageNow(1);
													setRestaurantPayload({
														...restaurantPayload,
														search: {
															"data_restaurants.name":
																!value && value === "" ? undefined : value,
															email: !value && value === "" ? undefined : value,
															mobile:
																!value && value === "" ? undefined : value,
														},
														page: 1,
													});
												}}
											/>
										</div>
									</div>
									<div className="col-sm-4">
										<div className="right-buttons d-flex justify-content-end">
											<button
												onClick={() => navigate(`${URL.updateRestaurant}/add`)}
												id="addNewRestaurant"
												type="button"
												className="btn glow-primary btn-primary"
											>
												<span className="bx bx-user-plus d-lg-none"></span>
												<span className="d-none d-sm-none d-lg-inline-block">
													Add Restaurant
												</span>
											</button>
											<button
												className="btn btn-default minW-md-0 btn-bg-white"
												onClick={() => downloadRestaurants(restaurantPayload)}
											>
												{isLoadingDownload ? "Downloading..." : "Download"}
											</button>
											{/* <Download columnKey={columnKey} /> */}
										</div>
									</div>
								</div>
							</div>
							<Table
								columnKey={columnKey}
								columns={columns}
								tableData={tableData}
								payload={restaurantPayload}
								sortCol={(obj) => {
									setPageNow(1);
									setRestaurantPayload({
										...restaurantPayload,
										sort: {
											[obj.alias]:
												!obj.sort && obj.sort === "" ? undefined : obj.sort,
										},
										page: 1,
									});
								}}
								setCurrentPage={(page) => {
									setPageNow(page);
									setRestaurantPayload({
										...restaurantPayload,
										page,
									});
								}}
								setCurrentLimit={(items_per_page) => {
									setPageNow(1);
									setItemsPerPage(items_per_page);
									setRestaurantPayload({
										...restaurantPayload,
										items_per_page,
										page: 1,
									});
								}}
								loading={isLoading}
								filterValue={async (obj) => {
									if (obj.filter === null) {
										const resp = await CS.filterData(
											restaurantPayload.filter,
											obj
										);
										setPageNow(1);
										setRestaurantPayload({
											...restaurantPayload,
											filter: resp,
											page: 1,
										});
									} else {
										setPageNow(1);
										setRestaurantPayload({
											...restaurantPayload,
											filter: {
												...restaurantPayload.filter,
												[obj.alias]: obj.filter,
											},
											page: 1,
										});
									}
								}}
								filterDate={(dates) => {
									setPageNow(1);
									setRestaurantPayload({
										...restaurantPayload,
										filter_date: dates && {
											...restaurantPayload?.filter_date,
											[dates.alias]: String(dates.filter),
										},
										page: 1,
									});
								}}
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default Restaurants;
